/**
 * FREE email-OTP implementation of OtpProvider (self-hosted — no SMS cost).
 *
 * Production: talks to the Apps Script web app (VITE_OTP_ENDPOINT — see
 * the retired Apps Script endpoint), which emailed a 6-digit code and, on verify, mints a
 * Firebase CUSTOM TOKEN signed by the project's service account. We then
 * signInWithCustomToken(). Works on web AND inside the Capacitor WebView (it's
 * a plain fetch — no reCAPTCHA, no deep links, no SMS).
 *
 * The request body is sent as text/plain so the browser treats it as a "simple
 * request" and skips the CORS preflight (Apps Script cannot answer OPTIONS).
 *
 * Emulator mode (VITE_USE_EMULATORS=true): short-circuits via emulatorAuth.ts —
 * any email works with the fixed code 123456.
 */
import { signInWithCustomToken, type UserCredential } from 'firebase/auth';
import { EMAIL_OTP_CODE_LENGTH, type EmailOtpBody, type EmailOtpResponse } from '@mitang/shared';
import { auth } from '../firebase';
import { otpEndpoint, useEmulators } from '../env';
import { EMULATOR_DEV_CODE, emulatorVerifyAndSignIn } from './emulatorAuth';
import type { OtpConfirmation, OtpProvider } from './types';

interface EmailConfirmation extends OtpConfirmation {
  readonly provider: 'email';
}

function normalizeEmail(raw: string): string {
  return raw.trim().toLowerCase();
}

export function isValidEmail(raw: string): boolean {
  const email = normalizeEmail(raw);
  return email.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/** Human copy per server error code (contracts.ts EmailOtpErrorCode). */
export function friendlyOtpError(res: Extract<EmailOtpResponse, { ok: false }>): string {
  switch (res.error) {
    case 'invalid_email':
      return 'Enter a valid email address.';
    case 'cooldown':
      return `Please wait ${res.retryAfterSec ?? 60}s before requesting another code.`;
    case 'rate_limited':
      return 'Too many codes requested for this email. Please try again later.';
    case 'quota_exhausted':
      return 'The daily sign-in email limit was reached. Please try again tomorrow.';
    case 'send_failed':
      return 'Could not send the email. Please try again.';
    case 'invalid_code':
      return 'Invalid code. Check the latest email and try again.';
    case 'expired':
      return 'That code has expired. Request a new one.';
    case 'too_many_attempts':
      return 'Too many attempts. Request a new code.';
    case 'invalid_token':
      return 'Sign-in could not be completed. Please try again.';
    default:
      return res.message || 'Something went wrong. Please try again.';
  }
}

export async function callOtpEndpoint(body: EmailOtpBody): Promise<EmailOtpResponse> {
  if (!otpEndpoint) {
    throw new Error(
      'Sign-in endpoint is not configured (VITE_OTP_ENDPOINT). Deploy the Apps Script web app and set its /exec URL.',
    );
  }
  const res = await fetch(otpEndpoint, {
    method: 'POST',
    // text/plain keeps this a CORS "simple request" — Apps Script can't answer
    // an OPTIONS preflight. The server JSON-parses the raw body regardless.
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify(body),
    redirect: 'follow', // Apps Script 302s to script.googleusercontent.com
  });
  if (!res.ok) {
    throw new Error(`Sign-in service unavailable (HTTP ${res.status}). Please try again.`);
  }
  return (await res.json()) as EmailOtpResponse;
}

export const emailOtpProvider: OtpProvider = {
  name: 'email',

  async sendOtp(emailRaw: string): Promise<OtpConfirmation> {
    const email = normalizeEmail(emailRaw);
    if (!isValidEmail(email)) throw new Error('Enter a valid email address.');

    if (useEmulators) {
      // eslint-disable-next-line no-console
      console.info(`[emailOtp] EMULATOR mode — use code ${EMULATOR_DEV_CODE} for ${email}`);
    } else {
      const res = await callOtpEndpoint({ action: 'request_otp', email });
      if (!res.ok) throw new Error(friendlyOtpError(res));
    }
    const confirmation: EmailConfirmation = { destination: email, provider: 'email' };
    return confirmation;
  },

  async verifyOtp(confirmation: OtpConfirmation, code: string): Promise<UserCredential> {
    if (confirmation.provider !== 'email') {
      throw new Error('Invalid confirmation handle for email provider.');
    }
    const email = confirmation.destination;
    const trimmed = code.trim();
    if (trimmed.length !== EMAIL_OTP_CODE_LENGTH) {
      throw new Error('Invalid code. Check the latest email and try again.');
    }

    if (useEmulators) {
      return emulatorVerifyAndSignIn(email, trimmed);
    }

    const res = await callOtpEndpoint({ action: 'verify_otp', email, code: trimmed });
    if (!res.ok) throw new Error(friendlyOtpError(res));
    if (!res.token) throw new Error('Sign-in service returned no token. Please try again.');
    return signInWithCustomToken(auth, res.token);
  },
};
